/**
 * Community Journals Hooks
 * Query shared journals for the Explore page
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CommunityJournal {
    id: string;
    name: string;
    community_description: string | null;
    tags: string[] | null;
    community_cover_url: string | null;
    view_count: number;
    copy_count: number;
    shared_at: string | null;
    created_at: string;
    user_id: string;
    // Creator info - fetched separately or from cache
    profiles?: {
        full_name: string | null;
        avatar_url: string | null;
    } | null;
}

/**
 * Fetch community journals (sharing_mode = 'community')
 * Optionally filter by tag
 */
export const useCommunityJournals = (selectedTag?: string) => {
    return useQuery({
        queryKey: ['community-journals', selectedTag],
        queryFn: async () => {
            // First, fetch the journals
            const { data: journals, error } = await supabase
                .from('journals')
                .select(`
          id,
          name,
          community_description,
          tags,
          community_cover_url,
          view_count,
          copy_count,
          shared_at,
          created_at,
          user_id
        `)
                .eq('sharing_mode', 'community')
                .order('view_count', { ascending: false });

            if (error) throw error;
            if (!journals || journals.length === 0) return [];

            // Fetch profile info for all users
            const userIds = [...new Set(journals.map(j => j.user_id))];
            const { data: profiles } = await supabase
                .from('public_profiles')
                .select('id, full_name, avatar_url')
                .in('id', userIds);

            // Create a map of user_id to profile
            const profileMap = new Map(
                profiles?.map(p => [p.id, { full_name: p.full_name, avatar_url: p.avatar_url }]) || []
            );

            // Merge profile info into journals
            const journalsWithProfiles: CommunityJournal[] = journals.map(journal => ({
                ...journal,
                view_count: journal.view_count ?? 0,
                copy_count: journal.copy_count ?? 0,
                profiles: profileMap.get(journal.user_id) || null,
            }));

            // Filter by tag on the client side if needed
            if (selectedTag && selectedTag !== 'recommend' && selectedTag !== 'featured') {
                return journalsWithProfiles.filter(journal =>
                    journal.tags?.includes(selectedTag)
                );
            }

            return journalsWithProfiles;
        },
    });
};

/**
 * Get all unique tags from community journals
 */
export const useCommunityJournalTags = () => {
    return useQuery({
        queryKey: ['community-journal-tags'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('journals')
                .select('tags')
                .eq('sharing_mode', 'community');

            if (error) throw error;

            // Extract all unique tags
            const tagsSet = new Set<string>();
            data?.forEach((journal: { tags: string[] | null }) => {
                journal.tags?.forEach((tag: string) => {
                    if (tag) tagsSet.add(tag);
                });
            });

            return Array.from(tagsSet).sort();
        },
    });
};

/**
 * Fetch a single community journal by ID
 */
export const useCommunityJournal = (journalId?: string) => {
    return useQuery({
        queryKey: ['community-journal', journalId],
        queryFn: async () => {
            if (!journalId) return null;

            // Fetch the journal
            const { data: journal, error } = await supabase
                .from('journals')
                .select('*')
                .eq('id', journalId)
                .eq('sharing_mode', 'community')
                .single();

            if (error) throw error;
            if (!journal) return null;

            // Fetch the creator's profile from public view
            const { data: profile } = await supabase
                .from('public_profiles')
                .select('full_name, avatar_url')
                .eq('id', journal.user_id)
                .maybeSingle();

            return {
                ...journal,
                profiles: profile || null,
            };
        },
        enabled: !!journalId,
    });
};

